import { Navigate } from 'react-router-dom';

import { call } from '@spacefill/shared/src/utils/Backend';
import { NavigateWithParams } from '@spacefill/shared/src/utils/RouterUtils';

const OrdersList = async () =>
    import('../../pages/orders/list/List').then((module) => ({
        Component: module.OrdersList,
    }));
const OrdersImport = async () =>
    import('../../pages/orders/new/ImportOrders').then((module) => ({
        Component: module.LogisticManagementOrdersImport,
    }));

const OrdersRedirectView = async () =>
    import('../../pages/orders/RedirectView').then((module) => ({
        Component: module.LogisticManagementOrdersRedirectView,
    }));
const WarehousesOrdersView = async () =>
    import('../../pages/orders/view/View').then((module) => ({
        Component: module.LogisticManagementWarehousesOrdersView,
    }));

const NewOrderInit = async () =>
    import('../../pages/orders/new/Init').then((module) => ({
        Component: module.NewOrderInit,
    }));
const WarehousesNewOrdersEntry = async () =>
    import('../../pages/orders/new/Entry').then((module) => ({
        Component: module.LogisticManagementWarehousesNewOrdersEntry,
    }));
const WarehousesNewOrdersExit = async () =>
    import('../../pages/orders/new/Exit').then((module) => ({
        Component: module.LogisticManagementWarehousesNewOrdersExit,
    }));
const NewSale = async () =>
    import('../../pages/orders/new/NewSale').then((module) => ({
        Component: module.NewSale,
    }));
const SalesList = async () =>
    import('../../pages/orders/list/SalesList').then((module) => ({
        Component: module.SalesList,
    }));

const OrdersRedirectEdit = async () =>
    import('../../pages/orders/RedirectEdit').then((module) => ({
        Component: module.LogisticManagementOrdersRedirectEdit,
    }));
const WarehousesEditOrders = async () =>
    import('../../pages/orders/edit/Edit').then((module) => ({
        Component: module.LogisticManagementWarehousesEditOrders,
    }));

const WarehousesOrdersAdjustments = async () =>
    import('../../pages/orders/Adjustments').then((module) => ({
        Component: module.LogisticManagementWarehousesOrdersAdjustments,
    }));
const UnconfirmedOrdersList = async () =>
    import('../../pages/unconfirmed-orders/List').then((module) => ({
        Component: module.LogisticManagementUnconfirmedOrdersList,
    }));
const UnconfirmedOrdersView = async () =>
    import('../../pages/unconfirmed-orders/View').then((module) => ({
        Component: module.LogisticManagementUnconfirmedOrdersView,
    }));

const AppointmentModalRoute = async () =>
    import('./AppointmentModalRoute').then((module) => ({
        Component: module.AppointmentModalRoute,
    }));

/*
 WARNING: if you modify this file, please check the regex on the context switch packages/external-console/src/utils/contextSwitch.js (const orderPagePatterns)
 */

const getOrderRoutes = (user) => {
    return [
        // LISTS — generic /orders/ redirects to /exit-orders/
        {
            path: '/orders/',
            element: <Navigate to='/exit-orders/' />,
        },
        {
            path: '/warehouses/:warehouseId/orders/',
            element: <NavigateWithParams to={(params) => `/warehouses/${params.warehouseId}/exit-orders/`} />,
        },
        {
            paths: [
                // Entries
                '/entry-orders/',
                '/entry-orders/view/:viewId',

                // Exits
                '/exit-orders/',
                '/exit-orders/view/:viewId',

                // Returns
                '/return-orders/',
                '/return-orders/view/:viewId',
            ],
            lazy: OrdersList,
            conditionFailedRedirect: '/',
            condition: !user?.carrierId,
        },

        // Backwards-compatible redirects for old warehouse-prefixed list URLs
        {
            paths: ['/warehouses/:warehouseId/entry-orders/', '/warehouses/:warehouseId/entry-orders/view/:viewId'],
            element: <Navigate to='/entry-orders/' />,
        },
        {
            paths: ['/warehouses/:warehouseId/exit-orders/', '/warehouses/:warehouseId/exit-orders/view/:viewId'],
            element: <Navigate to='/exit-orders/' />,
        },
        {
            paths: ['/warehouses/:warehouseId/return-orders/', '/warehouses/:warehouseId/return-orders/view/:viewId'],
            element: <Navigate to='/return-orders/' />,
        },

        // CREATION
        {
            paths: ['/entry-orders/init', '/exit-orders/init', '/return-orders/init', '/sales/init'],
            lazy: NewOrderInit,
            conditionFailedRedirect: '/',
            condition: !user?.carrierId,
        },
        {
            path: '/warehouses/:warehouseId/entry-orders/init',
            element: <NavigateWithParams to={(params) => `/entry-orders/init?warehouseId=${params.warehouseId}`} />,
        },
        {
            path: '/warehouses/:warehouseId/exit-orders/init',
            element: <NavigateWithParams to={(params) => `/exit-orders/init?warehouseId=${params.warehouseId}`} />,
        },
        {
            path: '/warehouses/:warehouseId/return-orders/init',
            element: <NavigateWithParams to={(params) => `/return-orders/init?warehouseId=${params.warehouseId}`} />,
        },
        {
            paths: ['/entry-orders/new', '/return-orders/new'],
            lazy: WarehousesNewOrdersEntry,
            conditionFailedRedirect: '/',
            condition: !user?.carrierId,
        },
        {
            paths: ['/warehouses/:warehouseId/entry-orders/new', '/warehouses/:warehouseId/orders/new-entry'],
            element: <NavigateWithParams to={(params) => `/entry-orders/new?warehouseId=${params.warehouseId}`} />,
        },
        {
            path: '/warehouses/:warehouseId/return-orders/new',
            element: <NavigateWithParams to={(params) => `/return-orders/new?warehouseId=${params.warehouseId}`} />,
        },
        {
            paths: ['/exit-orders/new'],
            lazy: WarehousesNewOrdersExit,
            conditionFailedRedirect: '/',
            condition: !user?.carrierId,
        },
        {
            paths: ['/warehouses/:warehouseId/exit-orders/new', '/warehouses/:warehouseId/orders/new-exit'],
            element: <NavigateWithParams to={(params) => `/exit-orders/new?warehouseId=${params.warehouseId}`} />,
        },

        // Shipper sales
        {
            paths: ['/sales/new'],
            lazy: NewSale,
            conditionFailedRedirect: '/',
            condition: !!user.customerId && user.features.displaySalesInApp && !user?.carrierId,
        },
        {
            paths: ['/sales/:saleId/edit'],
            lazy: NewSale,
            conditionFailedRedirect: '/',
            condition: !!user.customerId && user.features.displaySalesInApp && !user?.carrierId,
        },
        {
            paths: ['/sales/:saleId'],
            element: <NavigateWithParams to={(params) => `/sales/?saleId=${params.saleId}`} />,
            conditionFailedRedirect: '/',
            condition: !!user.customerId && user.features.displaySalesInApp && !user?.carrierId,
        },
        {
            paths: ['/sales/', '/sales/view/:viewId'],
            lazy: SalesList,
            conditionFailedRedirect: '/',
            condition: !!user.customerId && user.features.displaySalesInApp && !user?.carrierId,
        },

        // VIEW
        {
            paths: ['/orders/:orderId/', '/warehouses/:warehouseId/orders/:orderId/'],
            lazy: OrdersRedirectView,
            condition: !user?.carrierId,
        },

        {
            path: '/entry-orders/:orderId/',
            lazy: WarehousesOrdersView,
            children: [
                {
                    path: 'appointment/:appointmentId',
                    lazy: AppointmentModalRoute,
                    loader: appointmentFromAppointmentIdLoader,
                },
            ],
            redirectFrom: ['/warehouses/:warehouseId/entry-orders/:orderId/'],
        },
        {
            path: '/exit-orders/:orderId/',
            lazy: WarehousesOrdersView,
            children: [
                {
                    path: 'appointment/:appointmentId',
                    lazy: AppointmentModalRoute,
                    loader: appointmentFromAppointmentIdLoader,
                },
            ],
            redirectFrom: ['/warehouses/:warehouseId/exit-orders/:orderId/'],
        },
        {
            path: '/return-orders/:orderId/',
            lazy: WarehousesOrdersView,
            children: [
                {
                    path: 'appointment/:appointmentId',
                    lazy: AppointmentModalRoute,
                    loader: appointmentFromAppointmentIdLoader,
                },
            ],
            redirectFrom: ['/warehouses/:warehouseId/return-orders/:orderId/'],
        },

        // EDIT
        {
            path: '/orders/:orderId/edit',
            lazy: OrdersRedirectEdit,
            condition: !user?.carrierId,
        },
        {
            paths: ['/entry-orders/:orderId/edit', '/exit-orders/:orderId/edit'],
            lazy: WarehousesEditOrders,
            condition: !user?.carrierId,
        },
        {
            path: '/warehouses/:warehouseId/orders/:orderId/edit',
            element: <NavigateWithParams to={(params) => `/orders/${params.orderId}/edit`} />,
        },
        {
            path: '/warehouses/:warehouseId/entry-orders/:orderId/edit',
            element: <NavigateWithParams to={(params) => `/entry-orders/${params.orderId}/edit`} />,
        },
        {
            path: '/warehouses/:warehouseId/exit-orders/:orderId/edit',
            element: <NavigateWithParams to={(params) => `/exit-orders/${params.orderId}/edit`} />,
        },

        // Misc
        {
            path: '/orders/import/',
            lazy: OrdersImport,
            condition: !user?.carrierId,
        },

        // LSP only
        {
            path: '/unconfirmed-orders/',
            lazy: UnconfirmedOrdersList,
            condition: !!user.logisticProviderId && !user?.carrierId,
        },
        {
            path: '/unconfirmed-exits/',
            lazy: UnconfirmedOrdersList,
            condition: !!user.logisticProviderId && !user?.carrierId,
        },
        {
            path: '/unconfirmed-entries/',
            lazy: UnconfirmedOrdersList,
            condition: !!user.logisticProviderId && !user?.carrierId,
        },
        {
            paths: [
                '/unconfirmed-orders/:unconfirmedOrderId/',
                '/unconfirmed-entries/:unconfirmedOrderId/',
                '/unconfirmed-exits/:unconfirmedOrderId/',
            ],
            lazy: UnconfirmedOrdersView,
            condition: !!user.logisticProviderId && !user?.carrierId,
        },
        {
            paths: ['/entry-orders/:orderId/order-adjustment', '/exit-orders/:orderId/order-adjustment'],
            lazy: WarehousesOrdersAdjustments,
            condition: !!user.logisticProviderId && !user?.carrierId,
        },
        {
            path: '/warehouses/:warehouseId/entry-orders/:orderId/order-adjustment',
            element: <NavigateWithParams to={(params) => `/entry-orders/${params.orderId}/order-adjustment`} />,
        },
        {
            path: '/warehouses/:warehouseId/exit-orders/:orderId/order-adjustment',
            element: <NavigateWithParams to={(params) => `/exit-orders/${params.orderId}/order-adjustment`} />,
        },
    ];
};

const appointmentFromAppointmentIdLoader = async ({ params: { appointmentId } }) => {
    const appointment = await call(`appointments/${appointmentId}`);
    return { appointment };
};

export { getOrderRoutes };
